import type { PaginationQuery } from '../../../types/pagination';
import { appendPaginationQuery } from '../../../types/pagination';
import type { ApiClient } from '../client';
import type {
  AddTransportDriverDocumentRequest,
  AddTransportDriverDocumentResponse,
  CreateTransportAssignmentRequest,
  CreateTransportAssignmentResponse,
  CreateTransportBusRequest,
  CreateTransportBusResponse,
  CreateTransportDriverRequest,
  CreateTransportDriverResponse,
  CreateTransportRouteRequest,
  CreateTransportRouteResponse,
  CreateTransportRouteStopRequest,
  CreateTransportRouteStopResponse,
  ListStudentTransportsResponse,
  ListTransportAssignmentsResponse,
  ListTransportBusesResponse,
  ListTransportDriversResponse,
  ListTransportRouteStopsResponse,
  ListTransportRoutesResponse,
  SelectStudentTransportRequest,
  SelectStudentTransportResponse,
  TransportSettings,
  UpdateTransportAssignmentLocationRequest,
  UpdateTransportAssignmentLocationResponse,
  UpdateTransportRouteStopRequest,
  UpdateTransportSettingsRequest,
} from '../types';

export function bindTransport(client: ApiClient) {
  return {
    async listTransportRoutes() {
      return client.request<ListTransportRoutesResponse>('/transport/routes');
    },

    async createTransportRoute(body: CreateTransportRouteRequest) {
      return client.request<CreateTransportRouteResponse>('/transport/routes', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async listTransportRouteStops(routeId: string) {
      return client.request<ListTransportRouteStopsResponse>(
        `/transport/routes/stops?route_id=${encodeURIComponent(routeId)}`
      );
    },

    async createTransportRouteStop(body: CreateTransportRouteStopRequest) {
      return client.request<CreateTransportRouteStopResponse>('/transport/routes/stops', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async updateTransportRouteStop(stopId: string, body: UpdateTransportRouteStopRequest) {
      return client.request<void>(`/transport/routes/stops/${encodeURIComponent(stopId)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },

    async getTransportSettings() {
      return client.request<TransportSettings>('/transport/settings');
    },

    async updateTransportSettings(body: UpdateTransportSettingsRequest) {
      return client.request<TransportSettings>('/transport/settings', {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },

    async listTransportBuses() {
      return client.request<ListTransportBusesResponse>('/transport/buses');
    },

    async createTransportBus(body: CreateTransportBusRequest) {
      return client.request<CreateTransportBusResponse>('/transport/buses', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async listTransportDrivers() {
      return client.request<ListTransportDriversResponse>('/transport/drivers');
    },

    async createTransportDriver(body: CreateTransportDriverRequest) {
      return client.request<CreateTransportDriverResponse>('/transport/drivers', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async addTransportDriverDocument(body: AddTransportDriverDocumentRequest) {
      return client.request<AddTransportDriverDocumentResponse>('/transport/drivers/documents', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async listTransportAssignments(params?: PaginationQuery) {
      const qs = new URLSearchParams();
      appendPaginationQuery(qs, params);
      const query = qs.toString();
      return client.request<ListTransportAssignmentsResponse>(
        query ? `/transport/assignments?${query}` : '/transport/assignments'
      );
    },

    async createTransportAssignment(body: CreateTransportAssignmentRequest) {
      return client.request<CreateTransportAssignmentResponse>('/transport/assignments', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    /**
     * Publish last-known GPS for an assignment (driver app or admin QA).
     */
    async updateTransportAssignmentLocation(
      assignmentId: string,
      body: UpdateTransportAssignmentLocationRequest
    ) {
      return client.request<UpdateTransportAssignmentLocationResponse>(
        `/transport/assignments/${encodeURIComponent(assignmentId)}/location`,
        {
          method: 'POST',
          body: JSON.stringify(body),
        }
      );
    },

    async listStudentTransports(params?: PaginationQuery) {
      const qs = new URLSearchParams();
      appendPaginationQuery(qs, params);
      const query = qs.toString();
      return client.request<ListStudentTransportsResponse>(
        query ? `/transport/student-transports?${query}` : '/transport/student-transports'
      );
    },

    async selectStudentTransport(body: SelectStudentTransportRequest) {
      return client.request<SelectStudentTransportResponse>('/transport/student/opt-in', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
  };
}
